/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import { isEmpty } from 'lodash';
import React from 'react';

/**
 * Abstract component used to create module-level pop-up messages
 */
export default class ModuleMessageBase extends React.Component {
  constructor(props, classPrefix, titleText, bodyText) {
    super(props);
    this.classPrefix = classPrefix;
    this.titleText = titleText;
    this.bodyText = bodyText;

    this.componentAttached = this.componentAttached.bind(this);
  }

  shouldComponentUpdate(nextProps) {
    return this.showMessage(this.props) !== this.showMessage(nextProps);
  }

  hasData(props) {
    return props.stateProps.split(',').some((p) => !isEmpty(props[p]));
  }

  hasErrors(props) {
    const { loadStatus } = props;
    return props.stateProps
      .split(',')
      .some((p) => loadStatus[p] && loadStatus[p].error);
  }

  allLoaded(props) {
    const { loadStatus } = props;
    return props.stateProps
      .split(',')
      .every((p) => loadStatus[p] && loadStatus[p].loaded);
  }

  /**
   * Module is considered delayed loading if all modules have already loaded
   * at least once (or errored) and we've been loading this module over
   *
   * @param props
   * @returns {boolean}
   */
  isDelayedLoading(props) {
    if (this.hasErrors(props)) {
      return false;
    }

    const { loadStatus } = props;
    const stateProps = props.stateProps.split(',');

    const allEverLoaded = Object.keys(loadStatus)
      .map((key) => loadStatus[key])
      .filter((ls) => typeof ls === 'object' && ls !== null)
      .every((ls) => ls.everLoaded);

    if (!allEverLoaded) {
      return false;
    }
    const isDelayed = stateProps.some((p) => {
      const ls = loadStatus[p];
      return ls && ls.loadDelayed;
    });

    return isDelayed;
  }

  isGlobalLoading(props) {
    const { loadStatus } = props;

    if (loadStatus.errorCount) {
      return false;
    }

    if (
      loadStatus.accounts != undefined &&
      loadStatus.accounts.everLoaded &&
      loadStatus.history != undefined &&
      loadStatus.history.everLoaded &&
      loadStatus.activityWithCommentary != undefined &&
      loadStatus.activityWithCommentary.everLoaded
    ) {
      return false;
    }

    // special case, if we loaded accounts and there are none (APP-only client) then we will not request
    // commentary data
    if (
      loadStatus.accounts != undefined &&
      loadStatus.accounts.everLoaded &&
      props.accounts != undefined &&
      props.accounts.length === 0
    ) {
      return false;
    }

    //noinspection JSUnresolvedVariable
    return (
      loadStatus.accounts == undefined ||
      loadStatus.accounts.loading ||
      loadStatus.history == undefined ||
      loadStatus.history.loading ||
      loadStatus.activityWithCommentary == undefined ||
      loadStatus.activityWithCommentary.loading
    );
  }

  showMessage() {
    // child class must override
    return false;
  }

  getTitle() {
    // child can override
    return this.titleText;
  }

  /**
   * @param {HTMLElement} domElement
   */
  componentAttached(domElement) {
    // get null when removed
    if (domElement == null) {
      return;
    }

    const containerId = this.props.containerId;

    /** @type {HTMLElement} */
    const parentElement = containerId
      ? document.getElementById(containerId)
      : domElement.parentNode;

    const parentHeight = parentElement.clientHeight;

    domElement.style.width = parentElement.clientWidth + 'px';
    domElement.style.height = parentHeight + 'px';

    const content = domElement.firstElementChild;

    content.style.marginTop = '200px';
  }

  render() {
    if (!this.showMessage(this.props)) {
      return null;
    }

    const { classPrefix, bodyText } = this;

    const title = this.getTitle(this.props);

    return (
      <div className={classPrefix + '-container'} ref={this.componentAttached}>
        <div className={classPrefix + '-content'}>
          <div
            data-testid={classPrefix + '-title'}
            className={classPrefix + '-title'}>
            {title}
          </div>
          <div className={classPrefix + '-message'}>{bodyText}</div>
        </div>
      </div>
    );
  }
}
